import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, Linking, Platform, Alert, StyleSheet } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
  TrainingTopic,
  addTrainingTopic, updateTrainingTopic, deleteTrainingTopic,
  createTrainingEssential, updateTrainingEssential, fetchTrainingEssentials, deleteTrainingEssential,
} from '../../../../store/slices/adminSlice';
import DocumentPicker, { types } from 'react-native-document-picker';
import LHDataTable from '../../../../components/LHDataTable';
import LHProgressBar from '../../../../components/LHProgressBar';
import { createApiClient } from '../../../../api/apiClient';
import { AdminColors } from '../../constants/adminColors';
import { useToast } from '../../../../components/LhToastContainer';
import AdminTag from '../../components/AdminTag';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import AdminInfoBanner from '../../components/AdminInfoBanner';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import LhDatePicker from '../../../../components/LhDatePicker';
import RowActions from './RowActions';
import DeleteConfirmModal from './DeleteConfirmModal';
import EmptyState from './EmptyState';
import { styles } from './styles';
import { formatDate } from './helpers';
import { ACCEPTED_DOC_EXTENSIONS, MAX_DOC_SIZE_BYTES } from './constants';

const formatDisplayDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(isoMatch[2], 10) - 1]} ${parseInt(isoMatch[3], 10)}, ${isoMatch[1]}`;
  }
  return dateString;
};

const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export interface TrainingTabProps {
  showAdd: boolean;
  onCloseAdd: () => void;
}

const TrainingTab: React.FC<TrainingTabProps> = ({ showAdd, onCloseAdd }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const isAnyDark = resolvedTheme === 'dark';
  const datePickerColorScheme = resolvedTheme === 'dark' ? 'dark' : 'light';

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const training = useSelector((state: RootState) => state.configuration.homepageConfig.training);
  const [editItem, setEditItem] = React.useState<TrainingTopic | null>(null);
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [sequence, setSequence] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TrainingTopic | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const STATUS_ORDER: Record<string, number> = { Active: 0, Scheduled: 1, Expired: 2 };
  const sortedTraining = useMemo(() => [...training].sort((a, b) => {
    const seqDiff = (a.sequence ?? 0) - (b.sequence ?? 0);
    if (seqDiff !== 0) return seqDiff;
    const statusDiff = (STATUS_ORDER[a.status] ?? 2) - (STATUS_ORDER[b.status] ?? 2);
    if (statusDiff !== 0) return statusDiff;
    const dateDiff = (a.startDate ?? '').localeCompare(b.startDate ?? '');
    if (dateDiff !== 0) return dateDiff;
    return (a.title ?? '').localeCompare(b.title ?? '');
  }), [training]);

  React.useEffect(() => {
    if (editItem) {
      setTitle(editItem.title);
      setStartDate(editItem.startDate ? parseLocalDate(editItem.startDate) : null);
      setEndDate(editItem.endDate ? parseLocalDate(editItem.endDate) : null);
      setLinkUrl(editItem.linkUrl ?? '');
      setSequence(editItem.sequence !== undefined ? String(editItem.sequence) : '');
      if (editItem.fileUrl) {
        setUploadedFileUrl(editItem.fileUrl);
        // Extract a displayable filename from the blob URL (strip SAS token and path prefix)
        const blobPath = editItem.fileUrl.split('?')[0];
        const blobFileName = blobPath.split('/').pop() ?? 'Uploaded file';
        setUploadedFileName(blobFileName);
      } else {
        setUploadedFileUrl(null);
        setUploadedFileName(editItem.fileName ?? null);
      }
      setUploadedFile(null);
    } else if (!showAdd) {
      setTitle('');
      setStartDate(null);
      setEndDate(null);
      setLinkUrl('');
      setSequence('');
      setUploadedFileName(null);
      setUploadedFile(null);
      setUploadedFileUrl(null);
    }
    setFileUploadError(null);
  }, [editItem, showAdd]);

  const isDirty = editItem
    ? (
      title !== editItem.title ||
      formatDate(startDate) !== editItem.startDate ||
      formatDate(endDate) !== editItem.endDate ||
      linkUrl !== (editItem.linkUrl ?? '') ||
      sequence !== (editItem.sequence !== undefined ? String(editItem.sequence) : '') ||
      uploadedFileUrl !== (editItem.fileUrl ?? null) ||
      uploadedFileName !== (editItem.fileName ?? null)
    )
    : (!!title || !!startDate || !!endDate || !!linkUrl || !!sequence || !!uploadedFileName);

  const doClose = () => {
    onCloseAdd();
    setEditItem(null);
    setUploadedFileName(null);
    setUploadedFile(null);
    setUploadedFileUrl(null);
    setFileUploadError(null);
    setIsUploading(false);
    setUploadProgress(0);
    setIsSaving(false);
    setErrors({});
    setShowUnsavedWarning(false);
  };

  const handleClose = () => {
    if (isDirty) { setShowUnsavedWarning(true); return; }
    doClose();
  };

  const validateFile = (name: string, size: number | null | undefined): string | null => {
    if (size != null && size > MAX_DOC_SIZE_BYTES) {
      return 'File size must not exceed 10MB.';
    }
    const ext = '.' + name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_DOC_EXTENSIONS.includes(ext)) {
      return 'Invalid file type. Accepted: .pdf, .doc, .docx, .ppt, .pptx';
    }
    return null;
  };

  const uploadFile = async (file: any, name: string) => {
    setIsUploading(true);
    setUploadProgress(0);
    setFileUploadError(null);

    const formData = new FormData();
    formData.append('image', file);
    formData.append('containerName', 'ehs-training-docs');

    try {
      const api = createApiClient();
      const response = await api.post('/admin/images/upload', formData, {
        headers: { Accept: 'application/json' },
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent?.total) {
            const progress = Math.min(0.5, progressEvent.loaded / progressEvent.total);
            setUploadProgress(prev => prev === 0 ? Math.max(0.05, progress) : progress);
          }
        },
      });

      if (response?.data?.success === true) {
        setUploadedFileName(response.data.data?.originalFileName ?? name);
        setUploadedFile(file);
        setUploadedFileUrl(response.data.data?.imageUrl ?? null);
        setUploadProgress(1);
        setErrors(e => ({ ...e, linkOrFile: undefined as any }));
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        const errorMsg = response?.data?.message || 'Failed to upload file.';
        setFileUploadError(errorMsg);
      }
    } catch (e: any) {
      console.error('File upload error:', e);
      setFileUploadError('File size must not exceed 10MB.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = async () => {
    if (isUploading) return;
    setFileUploadError(null);

    if (Platform.OS === 'web') {
      const input = (globalThis as any).document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.doc,.docx,.ppt,.pptx';
      input.onchange = async (event: any) => {
        const file = event.target?.files?.[0];
        if (!file) return;
        const error = validateFile(file.name, file.size);
        if (error) { setFileUploadError(error); return; }
        await uploadFile(file, file.name);
      };
      input.click();
    } else {
      try {
        const result = await DocumentPicker.pick({
          type: [types.pdf, types.doc, types.docx, types.ppt, types.pptx],
          copyTo: 'cachesDirectory',
        });
        const picked = Array.isArray(result) ? result[0] : result;
        const error = validateFile(picked.name ?? '', picked.size);
        if (error) { setFileUploadError(error); return; }
        const fileObj = {
          uri: picked.fileCopyUri ?? picked.uri,
          name: picked.name ?? 'document',
          type: picked.type ?? 'application/octet-stream',
        };
        await uploadFile(fileObj as any, picked.name ?? 'document');
      } catch (err) {
        if (DocumentPicker.isCancel(err)) {
          // User cancelled — do nothing
        } else {
          console.error('Document picker error:', err);
          Alert.alert('Error', 'Failed to open file picker. Please try again.');
        }
      }
    }
  };

  const handleClearFile = () => {
    setUploadedFileName(null);
    setUploadedFile(null);
    setUploadedFileUrl(null);
    setFileUploadError(null);
    setIsUploading(false);
    setUploadProgress(0);
    if (errors.linkOrFile) setErrors(e => ({ ...e, linkOrFile: undefined as any }));
  };

  const handleSave = async () => {
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = 'Title is required.';
    if (!startDate) errs.startDate = 'Start Date is required.';
    if (!endDate) errs.endDate = 'End Date is required.';
    if (startDate && endDate && endDate < startDate) {
      errs.endDate = 'End Date must be on or after Start Date.';
    }
    if (linkUrl.trim() && uploadedFileName) {
      errs.linkOrFile = 'Please provide either a URL or a File — not both.';
    } else if (!linkUrl.trim() && !uploadedFileName) {
      errs.linkOrFile = 'Please provide either a Link URL or an uploaded File.';
    } else if (linkUrl.trim() && !uploadedFileName) {
      try {
        const parsed = new URL(linkUrl.trim());
        const hostname = parsed.hostname;
        const validTld = /\.(com|org|net|edu|gov|io|co|us|uk|ca|au|in|de|fr|jp|cn|info|biz|mil|int|app|dev|tech|online|store|site|web|media|news|health|finance|pro|live|club|shop|cloud|ai|tv|fm|me|ly|it|es|br|mx|nz|se|nl|pl|ru|za|ae|sg|hk|tw|kr|ar|ch|be|at|dk|fi|no|pt|cz|hu|ro|gr|il|th|vn|my|ph|pk|bd|ng|ke|gh|et|tz|ug|rw|eg|ma|dz|tn|sn|ci|cm|zw|zm|mz|ao|bj|tg|ml|ne|bf|gn|sl|lr|gm|ga|cg|cd|cf|td|sd|so|dj|er|na|bw|ls|sz|mg|mw|sc|mu|km|cv|st|mr)(\.[a-z]{2})?$/i;
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          errs.linkOrFile = 'URL must start with http:// or https://';
        } else if (!validTld.test(hostname)) {
          errs.linkOrFile = 'URL must have a valid domain (e.g. .com, .org, .net, etc.)';
        }
      } catch {
        errs.linkOrFile = 'Please enter a valid URL (e.g. https://example.com)';
      }
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    const start = formatDate(startDate);
    const end = formatDate(endDate);
    const seq = parseInt(sequence, 10) || undefined;
    if (editItem) {
      setIsSaving(true);
      const idx = training.findIndex((t) => t.id === editItem.id);
      const result = await dispatch(updateTrainingEssential({
        id: editItem.id,
        title: title.trim(),
        startDate: start,
        endDate: end,
        urlLinkForBanner: uploadedFileUrl || linkUrl.trim() || undefined,
        displayOrder: seq ?? (idx !== -1 ? idx + 1 : 1),
        locType: 'WH',
        subTitles: [],
      }));
      setIsSaving(false);
      if (updateTrainingEssential.fulfilled.match(result)) {
        // keep fileName in sync locally since API doesn't handle file
        if (uploadedFileName) {
          dispatch(updateTrainingTopic({ ...result.payload, fileName: uploadedFileName }));
        }
        dispatch(fetchTrainingEssentials());
        toast.showToast({ header: 'Success', subtext: 'Training topic updated successfully.', variant: 'success' });
        doClose();
      } else {
        toast.showToast({
          header: 'Error',
          subtext: (result.payload as string) || 'Failed to update training topic.',
          variant: 'error',
        });
      }
    } else {
      setIsSaving(true);
      const result = await dispatch(createTrainingEssential({
        title: title.trim(),
        startDate: start,
        endDate: end,
        urlLinkForBanner: uploadedFileUrl || linkUrl.trim() || undefined,
        displayOrder: seq ?? (training.length + 1),
        locType: 'WH',
        subTitles: [],
      }));
      setIsSaving(false);
      if (createTrainingEssential.fulfilled.match(result)) {
        toast.showToast({ header: 'Success', subtext: 'Training topic created successfully.', variant: 'success' });
        doClose();
      } else {
        toast.showToast({
          header: 'Error',
          subtext: (result.payload as string) || 'Failed to create training topic.',
          variant: 'error',
        });
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      setIsDeleting(true);
      const result = await dispatch(deleteTrainingEssential({ id: target.id }));
      if (deleteTrainingEssential.fulfilled.match(result)) {
        dispatch(fetchTrainingEssentials());
        setDeleteTarget(null);
        setTimeout(() => {
          toast.showToast({ header: 'Success', subtext: 'Training topic deleted successfully.', variant: 'success' });
        }, 300);
      } else {
        toast.showToast({
          header: 'Error',
          subtext: (result.payload as string) || 'Failed to delete training topic.',
          variant: 'error',
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const modalVisible = showAdd || editItem !== null;

  const trainingColumns = [
    {
      columnKey: 'sequence',
      header: 'Seq',
      renderCell: (item: TrainingTopic) => (
        <AdminTag label={`#${item.sequence ?? 1}`} variant="gray" small />
      ),
    },
    {
      columnKey: 'title',
      header: 'Title',
      flexGrow: 2,
      renderCell: (item: TrainingTopic) => (
        <Text style={{ color: isDark ? colors.text.primary : '#3A3A3A', fontSize: 14 }} numberOfLines={2} ellipsizeMode="tail">
          {item.title}
        </Text>
      ),
    },
    {
      columnKey: 'startDate',
      header: 'Start Date (CST)',
      flexGrow: 1.4,
      renderCell: (item: TrainingTopic) => (
        <Text style={{ color: isDark ? colors.text.primary : '#3A3A3A', fontSize: 14 }}>{formatDisplayDate(item.startDate)}</Text>
      ),
    },
    {
      columnKey: 'endDate',
      header: 'End Date (CST)',
      flexGrow: 1.4,
      renderCell: (item: TrainingTopic) => (
        <Text style={item.status === 'Expired' ? { color: '#DC2626', fontWeight: '600' as const, fontSize: 14 } : { color: isDark ? colors.text.primary : '#3A3A3A', fontSize: 14 }}>
          {formatDisplayDate(item.endDate)}
        </Text>
      ),
    },
    {
      columnKey: 'status',
      header: 'Status',
      flexGrow: 1,
      renderCell: (item: TrainingTopic) => (
        <AdminTag
          label={item.status}
          variant={item.status === 'Active' ? 'green' : item.status === 'Scheduled' ? 'blue' : 'orange'}
          small
        />
      ),
    },
    {
      columnKey: '_linkFile',
      header: 'Link/File',
      flexGrow: 1.4,
      renderCell: (item: TrainingTopic) => {
        const linkColor = isDark ? colors.brand.primary : AdminColors.blue700;
        if (item.linkUrl) {
          return (
            <TouchableOpacity onPress={() => Linking.openURL(item.linkUrl || '')} activeOpacity={0.7}>
              <Text style={{ color: linkColor, fontSize: 12, textDecorationLine: 'underline' }}>↗ Link</Text>
            </TouchableOpacity>
          );
        }
        if (item.fileUrl) {
          return (
            <TouchableOpacity onPress={() => Linking.openURL(item.fileUrl || '')} activeOpacity={0.7}>
              <Text style={{ color: linkColor, fontSize: 12, textDecorationLine: 'underline' }}>📄 File</Text>
            </TouchableOpacity>
          );
        }
        if (item.fileName) {
          return <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : AdminColors.textSecondary }}>📄 {item.fileName}</Text>;
        }
        return null;
      },
    },
    {
      columnKey: '_actions',
      header: 'Actions',
      flexGrow: 1,
      sortable: false,
      renderCell: (item: TrainingTopic) => (
        <RowActions onEdit={() => setEditItem(item)} onDelete={() => setDeleteTarget(item)} />
      ),
    },
  ];

  return (
    <View>
      {training.length === 0 ? (
        <EmptyState />
      ) : (
        <LHDataTable
          data={sortedTraining}
          columns={trainingColumns}
          tableHeader=""
          tableDescription={`Showing ${training.length} result(s)`}
          onRowClick={() => { /* Disable row click overlay */ }}
          disableRowHighlight
        />
      )}

      <AdminModal
        visible={modalVisible}
        title={editItem ? 'Edit Training Topic' : 'Add New Training Topic'}
        onClose={handleClose}
        footer={
          <>
            <AdminButton label="Cancel" variant="secondary" onPress={doClose} />
            <AdminButton label={isSaving ? 'Saving...' : editItem ? 'Save Changes' : 'Save'} variant="primary" onPress={handleSave} />
          </>
        }
      >
        {showUnsavedWarning && (
          <View
            style={[
              trainingLocalStyles.warningBanner,
              isAnyDark && {
                backgroundColor: colors.statusBackground.warning,
                borderColor: colors.status.warning,
              },
            ]}
          >
            <View style={trainingLocalStyles.warningBannerContent}>
              <Text style={[trainingLocalStyles.warningIcon, isAnyDark && { color: colors.status.warning }]}>⚠</Text>
              <Text style={[trainingLocalStyles.warningMessage, isAnyDark && { color: colors.status.warning }]}>
                You have unsaved changes. To discard and close, click{' '}
                <Text style={trainingLocalStyles.warningMessageBold}>Cancel</Text>.
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowUnsavedWarning(false)} style={trainingLocalStyles.warningCloseBtn}>
              <Text style={[trainingLocalStyles.warningCloseText, isAnyDark && { color: colors.status.warning }]}>✕</Text>
            </TouchableOpacity>
          </View>
        )}
        <AdminFormField label="Title" required hint="Max 50 characters" error={errors.title}>
          <AdminTextInput
            placeholder="Fire Extinguisher Refresher"
            maxLength={50}
            value={title}
            onChangeText={(v) => { setTitle(v); if (errors.title) setErrors(e => ({ ...e, title: undefined as any })); }}
            hasError={!!errors.title}
          />
        </AdminFormField>
        <AdminFormField label="Display Sequence" hint="Controls the order topics appear on the homepage">
          <AdminTextInput placeholder="1" keyboardType="number-pad" value={sequence} onChangeText={setSequence} />
        </AdminFormField>
        <View style={styles.formRow}>
          <View style={styles.formHalf}>
            <AdminFormField label="Start Date" required hint="CST" error={errors.startDate}>
              <LhDatePicker
                value={startDate}
                onChange={(date) => { setStartDate(date); if (errors.startDate) setErrors(e => ({ ...e, startDate: undefined as any })); }}
                placeholder="Select Start Date"
                colorScheme={isAnyDark ? 'dark' : 'light'}
                iconColor={isAnyDark ? colors.text.primary : '#555'}
                disabledIconColor={isAnyDark ? colors.text.disabled : '#bdbdbd'}
              />
            </AdminFormField>
          </View>
          <View style={styles.formHalf}>
            <AdminFormField label="End Date" required hint="CST — topic auto-removed after this date" error={errors.endDate}>
              <LhDatePicker
                value={endDate}
                onChange={(date) => { setEndDate(date); if (errors.endDate) setErrors(e => ({ ...e, endDate: undefined as any })); }}
                placeholder="Select End Date"
                minimumDate={startDate ?? undefined}
                colorScheme={isAnyDark ? 'dark' : 'light'}
                iconColor={isAnyDark ? colors.text.primary : '#555'}
                disabledIconColor={isAnyDark ? colors.text.disabled : '#bdbdbd'}
              />
            </AdminFormField>
          </View>
        </View>
        <AdminInfoBanner message="Use URL or File — not both. At least one is required." variant="info" />
        {errors.linkOrFile ? (
          <Text style={trainingLocalStyles.linkOrFileError}>{errors.linkOrFile}</Text>
        ) : null}
        <AdminFormField label="Link URL" labelSuffix="Optional" hint="Valid domain required (.com, .org, etc.)">
          <AdminTextInput
            placeholder="https://..."
            keyboardType="url"
            value={linkUrl}
            onChangeText={(v) => { setLinkUrl(v); if (errors.linkOrFile) setErrors(e => ({ ...e, linkOrFile: undefined as any })); }}
          />
        </AdminFormField>
        <AdminFormField label="Upload File" labelSuffix="Optional — .pdf, .doc, .docx, .ppt, .pptx · max 10MB">
          {isUploading ? (
            <View style={[styles.fileUploadArea, styles.fileUploadAreaUploading]}>
              <View style={styles.fileUploadProgressContainer}>
                <LHProgressBar progress={uploadProgress} height={16} />
                <Text style={styles.fileUploadProgressText}>
                  {uploadProgress >= 0.99 ? 'Processing...' : `${Math.round(uploadProgress * 100)}%`}
                </Text>
              </View>
            </View>
          ) : (
            <TouchableOpacity
              style={[
                styles.fileUploadArea,
                uploadedFileName ? styles.fileUploadAreaSelected : null,
                fileUploadError ? styles.fileUploadAreaError : null,
                isAnyDark && !uploadedFileName && !fileUploadError && {
                  borderColor: colors.border.default,
                  backgroundColor: colors.background.elevated,
                },
                isAnyDark && uploadedFileName && !fileUploadError && {
                  borderColor: colors.brand.primary,
                  backgroundColor: colors.background.surface,
                },
              ]}
              onPress={handleFileSelect}
              activeOpacity={0.7}
            >
              {uploadedFileName ? (
                <View style={styles.fileUploadSelectedRow}>
                  <Text style={[styles.fileUploadSelectedName, isAnyDark && { color: colors.text.primary }]} numberOfLines={1}>📄 {uploadedFileName}</Text>
                  <TouchableOpacity
                    style={styles.fileUploadClearBtn}
                    onPress={handleClearFile}
                    activeOpacity={0.7}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <Text style={[styles.fileUploadClearText, isAnyDark && { color: colors.text.secondary }]}>Remove</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <>
                  <Text style={[styles.fileUploadText, isAnyDark && { color: colors.text.secondary }]}>Tap to select file</Text>
                  <Text style={[styles.fileUploadSub, isAnyDark && { color: colors.text.disabled }]}>.pdf, .doc, .docx, .ppt, .pptx accepted</Text>
                </>
              )}
            </TouchableOpacity>
          )}
          {fileUploadError ? (
            <Text style={styles.fileUploadErrorText}>{fileUploadError}</Text>
          ) : null}
        </AdminFormField>
      </AdminModal>

      <DeleteConfirmModal
        visible={deleteTarget !== null}
        itemLabel={deleteTarget?.title ?? ''}
        message="Are you sure you want to delete this topic?"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
    </View>
  );
};

export default TrainingTab;

const trainingLocalStyles = StyleSheet.create({
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: '#FFF3E0',
    borderWidth: 1,
    borderColor: '#FFB74D',
    borderRadius: 6,
    marginBottom: 12,
    padding: 12,
  },
  warningBannerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  warningIcon: {
    fontSize: 15,
    color: '#E65100',
  },
  warningMessage: {
    flex: 1,
    fontSize: 13,
    color: '#E65100',
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  warningMessageBold: {
    fontWeight: '700',
    fontFamily: FontFamily.medium,
  },
  warningCloseBtn: {
    padding: 2,
    marginLeft: 8,
  },
  warningCloseText: {
    fontSize: 14,
    color: '#E65100',
    fontWeight: '700',
  },
  linkOrFileError: {
    fontSize: 12,
    color: '#C12C01',
    marginBottom: 8,
    fontFamily: FontFamily.regular,
  },
});
